import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MessageCircle, Lock, ShieldCheck, Truck } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { productImage } from "@/components/ProductGrid";
import { waLink } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState("loading");
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        setState("found");
      })
      .catch(() => setState("error"));
  }, [id]);

  const hasPrice = product && /\d/.test(product.price || "");

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        product_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Zahlung konnte nicht gestartet werden.");
      setBuying(false);
    }
  };

  if (state === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-wine text-cream/50">Lade…</div>;
  }

  if (state === "error" || !product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-wine px-5 text-center">
        <p className="font-serif text-3xl text-cream">Dieses Stück gibt es nicht (mehr).</p>
        <Link
          to="/"
          data-testid="detail-back-home"
          className="rounded-full border border-gold px-8 py-3 text-sm font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
        >
          Zur Kollektion
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wine text-cream">
      <header className="border-b border-gold/10 bg-wine/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" data-testid="detail-logo" className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <span className="leading-none">
              <span className="block font-serif text-2xl tracking-tight">
                APSARA<span className="text-gold">.</span>
              </span>
              <span className="mt-1 block text-[0.55rem] font-light uppercase tracking-[0.4em] text-gold/90">
                Kollektion
              </span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="detail-back"
            className="flex items-center gap-2 text-xs font-light uppercase tracking-[0.25em] text-cream/60 transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4" /> Zurück
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-12 md:px-10 lg:grid-cols-2 lg:py-20">
        <div>
          <div className="overflow-hidden rounded-lg border border-white/10 bg-wine2">
            {productImage(product.image) ? (
              <img
                src={productImage(product.image)}
                alt={product.name}
                className="max-h-[80vh] min-h-[320px] w-full object-cover"
                data-testid="detail-image"
              />
            ) : (
              <div className="flex h-[60vh] items-center justify-center text-cream/30">Kein Bild</div>
            )}
          </div>
        </div>

        <div className="flex flex-col justify-center">
            <p className="text-xs font-light uppercase tracking-[0.35em] text-gold" data-testid="detail-eyebrow">
              {product.type === "suit"
                ? "02 — Suits"
                : product.type === "dupatta"
                ? "03 — Dupatta"
                : "01 — Schmuck"}{" "}
              · Anzeige
            </p>
          <h1
            className="mt-4 font-serif text-4xl font-light leading-tight tracking-[-0.02em] md:text-6xl"
            data-testid="detail-name"
          >
            {product.name}
          </h1>
          <p className="mt-5 font-serif text-3xl text-gold md:text-4xl" data-testid="detail-price">
            {product.price || "Preis auf Anfrage"}
          </p>

          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-rosy" data-testid="detail-description">
            {product.description ||
              "Handverlesenes Stück aus der APSARA Kollektion — mit Liebe ausgewählt und von Hand veredelt. Schreib uns für Details, Maße oder mehr Fotos."}
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            {hasPrice && (
              <button
                onClick={buy}
                disabled={buying}
                className="flex items-center justify-center gap-3 rounded-full bg-gold px-9 py-4 text-sm font-medium uppercase tracking-widest text-wine transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                data-testid="detail-buy-button"
              >
                <Lock className="h-4 w-4" />
                {buying ? "Weiter zu Stripe…" : "Mit Kreditkarte kaufen"}
              </button>
            )}
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-whatsapp"
              className="flex items-center justify-center gap-2 rounded-full border border-gold/60 px-9 py-4 text-sm font-medium uppercase tracking-widest text-gold transition-colors duration-300 hover:bg-gold hover:text-wine"
            >
              <MessageCircle className="h-4 w-4" />
              Per WhatsApp anfragen
            </a>
          </div>

          <div className="mt-10 space-y-3 border-t border-gold/15 pt-8">
            {[
              { icon: ShieldCheck, text: "Sichere Zahlung via Stripe — Kreditkarte, testweise" },
              { icon: Truck, text: "Versand nach Absprache, sorgfältig verpackt" },
            ].map(({ icon: Icon, text }) => (
              <p key={text} className="flex items-center gap-3 text-sm font-light text-cream/60">
                <Icon className="h-4 w-4 shrink-0 text-gold" />
                {text}
              </p>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
